// ** React Imports
import { memo, useMemo, useRef } from "react";
import { useHistory } from "react-router-dom";

// ** Redux
import { useDispatch, useSelector } from "react-redux";
import { openAuthModal } from "store/modules/global-common.slice";

// ** Custom Components
import { Image } from "components/media";
import { StyledMasonryCardWrapperNew } from "./styles/MasonryCardWrapperNew.styled";
import UserMediaSkeletor from "components/user/UserMediaSkeletor";
import { FakeActionButton } from "../FakeActionButton";
import DatePresenter from "components/other/DatePresenter";
import Text from "components/typography/Text";
import Block from "components/layout/Block";

// ** Utils & Helpers
import generatePath from "utils/generatePath";
import pseudoFormat from "utils/pseudoFormat";

// ** Third Party Components
import { useInView } from "react-intersection-observer";

// ** Routes
import containers from "containers";

// #####################################################

const MasonryImageNew = ({
	withoutLazyLoading = false,
	contentId,
	contentHeight,
	contentWidth,
	contentAlbum,
	contentType = "photo",
	contentLikesCount = 0,
	contentCommentsCount = 0,
	imageVert = "",
	userId,
	userLogin,
	userAvatar,
	userOnline,
	userStatusVip,
	contentData,
	isUserGallery,
	locationSearch = "",
}) => {
	const dispatch = useDispatch();
	const history = useHistory();

	const isAuth = useSelector((state) => state.globalCommon.isAuth);

	const to = useMemo(() => {
		const generatedPath = generatePath(containers.modal.path, {
			contentId,
			login: userLogin?.toLowerCase(),
			type: contentType,
			album: contentAlbum,
		})?.toLowerCase();
		return { hash: generatedPath, search: locationSearch };
	}, [isAuth, contentId, userLogin, containers.modal.path]);

	const inlineStyledMemonized = useMemo(() => {
		return { width: "100%", position: "relative" };
	}, []);

	const { ref, inView } = useInView({
		threshold: 0,
		triggerOnce: false,
		rootMargin: "100px 0px 100px 0px",
		initialInView: true,
	});

	const containerRef = useRef(null);
	const lastHeightRef = useRef(null);

	const updateHeight = () => {
		if (containerRef.current) {
			const currentHeight = containerRef.current.offsetHeight;
			if (lastHeightRef.current !== currentHeight) {
				lastHeightRef.current = currentHeight;
			}
		}
	};
	if (inView) {
		updateHeight();
	}

	const combineRef = (el) => {
		containerRef.current = el;
		ref(el);
	};

	const memoizedUser = useMemo(() => {
		return {
			id: userId,
			avatar: userAvatar,
			login: userLogin,
			status_vip: userStatusVip,
			online: userOnline,
		};
	}, []);

	const likesUnderMasonryRender = () => {
		if (isUserGallery) {
			return (
				<Block
					style={inlineStyledMemonized}
					onClick={() => {
						if (!isAuth) {
							dispatch(openAuthModal());
						}
					}}
				>
					<div
						style={{
							display: "flex",
							flexWrap: "wrap",
							margin: "0.5rem 0.75rem",
							justifyContent: "space-between",
							width: "calc(100% - 1.5rem)",
						}}
					>
						<Block
							mr={1}
							style={{
								marginTop: "-1px",
								position: "relative",
							}}
						>
							<Text
								color={"grey"}
								style={{ verticalAlign: "bottom" }}
								size={"0.8rem"}
							>
								<DatePresenter dateOnly date={contentData} />
							</Text>
						</Block>
						<Block flex alignCenter textNowrap>
							<FakeActionButton
								isUserGalleryProfile
								isHeart
								label={
									contentLikesCount > 0
										? pseudoFormat(contentLikesCount)
										: null
								}
								labelSize="0.8rem"
								customSvgSize="11px"
								customMargin="0 0 0 0"
								customMarginLeft="2px"
								customPadding="0"
							/>
						</Block>
					</div>
				</Block>
			);
		}

		return (
			<div
				style={{
					display: "flex",
					flexWrap: "wrap",
					margin: "0.5rem 0.75rem",
				}}
			>
				<FakeActionButton
					isHeart
					label={
						contentLikesCount > 0
							? pseudoFormat(contentLikesCount)
							: null
					}
					labelSize="0.8rem"
					customSvgSize="15px"
					customMargin="0 1rem 0 0"
					customPadding="0"
				/>
				<FakeActionButton
					isComment
					labelSize="0.8rem"
					label={
						contentCommentsCount > 0
							? pseudoFormat(contentCommentsCount)
							: null
					}
					customSvgSize="15px"
					customMargin="0 0 0 0"
					customPadding="0"
				/>
			</div>
		);
	};

	// #####################################################

	return (
		<StyledMasonryCardWrapperNew
			ref={combineRef}
			style={{
				minHeight: inView
					? "auto"
					: lastHeightRef.current > 0
					? lastHeightRef.current
					: "100px",
			}}
		>
			{inView ? (
				<Block
					style={inlineStyledMemonized}
					onClick={(e) => {
						if (e.target.closest("a")) {
							return;
						}

						if (!isAuth) {
							dispatch(openAuthModal());
						} else {
							history.push(to);
						}
					}}
				>
					{!isUserGallery && (
						<div
							style={{
								margin: "0.5rem 0.75rem",
								width: "calc(100% - 1.5rem)",
							}}
						>
							<UserMediaSkeletor
								alignCenter
								noMarginBottom
								user={memoizedUser}
								size="xxxsmall"
								vipProps={{
									height: 11.2,
									marginBottom: 0.125,
								}}
								gapAvatarLogin={2}
								noOnlineCircle
							/>
						</div>
					)}

					<Image
						thumbnail={imageVert}
						alt=""
						width={contentWidth}
						height={contentHeight}
						zoom
						withoutLazyLoading={withoutLazyLoading}
						isCover
						withRatio_4_3
						login={userLogin}
					/>

					{likesUnderMasonryRender()}
				</Block>
			) : null}
		</StyledMasonryCardWrapperNew>
	);
};

export default memo(MasonryImageNew);
